import * as React from 'react';
import {
  Button,
  Input,
  Select,
  Switch,
  makeStyles,
  tokens,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import { Integration, IntegrationCreate } from '../../../types/integration';
import { ApiErrorInfo } from '../../../types/api';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalL,
  },
});

interface IntegrationFormProps {
  integration?: Integration;
  onSave: (integration: IntegrationCreate) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: ApiErrorInfo | null;
}

const IntegrationForm: React.FC<IntegrationFormProps> = ({
  integration,
  onSave,
  onCancel,
  loading,
  error
}) => {
  const styles = useStyles();

  const [formData, setFormData] = React.useState<IntegrationCreate>(
    integration ? {
      erp: integration.erp,
      empresa: integration.empresa,
      urlIntegracao: integration.urlIntegracao,
      autenticacaoAD: integration.autenticacaoAD ?? false,
      usuarioAD: integration.usuarioAD ?? null,
      usuarioERP: integration.usuarioERP,
      senhaERP: '', // Não preencher senha na edição por segurança
    } : {
      erp: 1,
      empresa: '',
      urlIntegracao: '',
      autenticacaoAD: false,
      usuarioAD: null,
      usuarioERP: '',
      senhaERP: '',
    }
  );

  const [fieldErrors, setFieldErrors] = React.useState({
    empresa: '',
    urlIntegracao: '',
    usuarioAD: '',
    usuarioERP: '',
    senhaERP: '',
  });

  const [touchedFields, setTouchedFields] = React.useState({
    empresa: false,
    urlIntegracao: false,
    usuarioAD: false,
    usuarioERP: false,
    senhaERP: false,
  });

  const validateField = (field: keyof typeof fieldErrors, value: string, touched: boolean = false) => {
    let error = '';
    
    switch (field) {
      case 'empresa':
        if (!value.trim()) {
          error = 'Empresa é obrigatória';
        }
        break;
      case 'urlIntegracao':
        if (!value.trim()) {
          error = 'URL de integração é obrigatória';
        } else if (!/^https?:\/\/.+/.test(value)) {
          error = 'URL deve começar com http:// ou https://';
        }
        break;
      case 'usuarioERP':
        if (!value.trim()) {
          error = 'Usuário do ERP é obrigatório';
        }
        break;
      case 'usuarioAD':
        if (formData.autenticacaoAD && !value.trim()) {
          error = 'Usuário AD é obrigatório quando autenticação AD está ativa';
        } else if (value.trim() && value.trim().length > 50) {
          error = 'Usuário AD deve ter no máximo 50 caracteres';
        }
        break;
      case 'senhaERP':
        if (!value.trim() && !integration) {
          error = 'Senha do ERP é obrigatória';
        }
        break;
    }
    
    setFieldErrors(prev => ({ ...prev, [field]: touched ? error : '' }));
    return error === '';
  };

  // Verificar se o formulário é válido
  const isFormValid = React.useMemo(() => {
    // Verificar se há erros de validação apenas em campos tocados
    const hasErrors = Object.entries(fieldErrors).some(([field, error]) => 
      error !== '' && touchedFields[field as keyof typeof touchedFields]
    );
    
    // Verificar se campos obrigatórios estão preenchidos (sempre verificar, independente de touched)
    const hasRequiredFields = formData.empresa.trim() && 
                             formData.urlIntegracao.trim() && 
                             (!formData.autenticacaoAD || (formData.usuarioAD ?? '').trim()) &&
                             formData.usuarioERP.trim() &&
                             (integration || formData.senhaERP.trim());
    
    return !hasErrors && hasRequiredFields;
  }, [fieldErrors, touchedFields, formData, integration]);

  const handleInputChange = (field: keyof IntegrationCreate, value: string | number | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validar campo em tempo real apenas se já foi tocado
    if (field in touchedFields && touchedFields[field as keyof typeof touchedFields]) {
      const valueAsString = typeof value === 'string' ? value : '';
      validateField(field as keyof typeof fieldErrors, valueAsString, true);
    }

    if (field === 'autenticacaoAD') {
      const isEnabled = Boolean(value);
      const nextUsuarioAD = isEnabled ? formData.usuarioAD ?? '' : null;
      setFormData(prev => ({
        ...prev,
        autenticacaoAD: isEnabled,
        usuarioAD: nextUsuarioAD,
      }));

      if (!isEnabled) {
        setFieldErrors(prev => ({ ...prev, usuarioAD: '' }));
        setTouchedFields(prev => ({ ...prev, usuarioAD: false }));
      }
    }
  };

  const handleBlur = (field: keyof typeof fieldErrors) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    const value = formData[field as keyof IntegrationCreate];
    validateField(field, typeof value === 'string' ? value : '', true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Marcar todos os campos como tocados e validar
    setTouchedFields({
      empresa: true,
      urlIntegracao: true,
      usuarioAD: true,
      usuarioERP: true,
      senhaERP: true,
    });

    // Validar todos os campos
    const isEmpresaValid = validateField('empresa', formData.empresa, true);
    const isUrlValid = validateField('urlIntegracao', formData.urlIntegracao, true);
    const isUsuarioADValid = validateField('usuarioAD', formData.usuarioAD ?? '', true);
    const isUsuarioValid = validateField('usuarioERP', formData.usuarioERP, true);
    const isSenhaValid = validateField('senhaERP', formData.senhaERP, true);

    // Se algum campo for inválido, não prosseguir
    if (!isEmpresaValid || !isUrlValid || !isUsuarioADValid || !isUsuarioValid || !isSenhaValid) {
      return;
    }

    onSave({
      ...formData,
      autenticacaoAD: Boolean(formData.autenticacaoAD),
      usuarioAD: formData.autenticacaoAD && (formData.usuarioAD ?? '').trim()
        ? (formData.usuarioAD ?? '').trim().slice(0, 50)
        : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      {loading && (
        <MessageBar intent="info">
          <MessageBarBody>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <Spinner size="tiny" />
              Estamos confirmando a URL, usuário e senha da integração. Isso pode levar alguns segundos...
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent={error.isAuthError ? "warning" : "error"}>
          <MessageBarBody>
            {error.isNotFound && "Integração não encontrada. Verifique se o ID está correto."}
            {error.isValidationError && error.message}
            {error.isAuthError && "Acesso negado. Você não tem permissão para esta operação."}
            {!error.isNotFound && !error.isValidationError && !error.isAuthError && error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.section}>
        <Field
          label="Sistema ERP"
          required
        >
          <Select
            size="large"
            value={formData.erp.toString()}
            onChange={(e, data) => handleInputChange('erp', parseInt(data.value || '1'))}
          >
            <option value="1">ERP UAU</option>
          </Select>
        </Field>

        <Field
          label="Empresa"
          required
          validationMessage={fieldErrors.empresa}
          validationState={fieldErrors.empresa ? 'error' : 'none'}
        >
          <Input
            size="large"
            value={formData.empresa}
            onChange={(e) => handleInputChange('empresa', e.target.value)}
            onBlur={() => handleBlur('empresa')}
            placeholder="Nome da empresa"
          />
        </Field>

        <Field
          label="URL de Integração"
          required
          validationMessage={fieldErrors.urlIntegracao}
          validationState={fieldErrors.urlIntegracao ? 'error' : 'none'}
        >
          <Input
            size="large"
            type="url"
            value={formData.urlIntegracao}
            onChange={(e) => handleInputChange('urlIntegracao', e.target.value)}
            onBlur={() => handleBlur('urlIntegracao')}
            placeholder="https://api.empresa.com/integracao"
          />
        </Field>

        <Field label="Autenticação AD">
          <Switch
            checked={Boolean(formData.autenticacaoAD)}
            onChange={(e) => handleInputChange('autenticacaoAD', e.currentTarget.checked)}
            label={formData.autenticacaoAD ? 'Ativa' : 'Inativa'}
          />
        </Field>

        {formData.autenticacaoAD && (
          <Field
            label="Usuário AD"
            required
            validationMessage={fieldErrors.usuarioAD}
            validationState={fieldErrors.usuarioAD ? 'error' : 'none'}
          >
            <Input
              size="large"
              value={formData.usuarioAD ?? ''}
              onChange={(e) => handleInputChange('usuarioAD', e.target.value)}
              onBlur={() => handleBlur('usuarioAD')}
              placeholder="Usuário AD"
              maxLength={50}
            />
          </Field>
        )}

        <Field
          label="Usuário do ERP"
          required
          validationMessage={fieldErrors.usuarioERP}
          validationState={fieldErrors.usuarioERP ? 'error' : 'none'}
        >
          <Input
            size="large"
            value={formData.usuarioERP}
            onChange={(e) => handleInputChange('usuarioERP', e.target.value)}
            onBlur={() => handleBlur('usuarioERP')}
            placeholder="Usuário de acesso ao ERP"
          />
        </Field>

        <Field
          label={integration ? "Nova Senha do ERP (opcional)" : "Senha do ERP"}
          required={!integration}
          validationMessage={fieldErrors.senhaERP}
          validationState={fieldErrors.senhaERP ? 'error' : 'none'}
        >
          <Input
            size="large"
            type="password"
            value={formData.senhaERP}
            onChange={(e) => handleInputChange('senhaERP', e.target.value)}
            onBlur={() => handleBlur('senhaERP')}
            placeholder="Senha de acesso ao ERP"
          />
        </Field>
      </div>

      <div className={styles.actions}>
        <Button appearance="subtle" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          type="submit"
          appearance="primary"
          disabled={loading || !isFormValid}
        >
          {loading ? 'Salvando...' : integration ? 'Atualizar' : 'Criar'} Integração
        </Button>
      </div>
    </form>
  );
};

export default IntegrationForm;